/**
 * Global Organization Manager
 * ===========================
 *
 * Master-Org-Admin-only console for managing the global pool of media
 * owners, broadcasters, and their underlying /organizations docs.
 *
 * Tabs:
 *   - Media Owners — list every entity in /mediaowner. Create, rename,
 *     change type, change owner, delete (orphans broadcasters parented to
 *     this one).
 *   - Broadcasters — list every entity in /broadcaster. Same operations
 *     plus a parent-media-owner selector and accepted-languages editor.
 *   - Change Log — append-only audit of every action taken on this page.
 *
 * All mutations route through `src/services/globalOrgManager.ts`, which
 * writes the canonical doc, cascades to the matching link doc, updates
 * member subcollections + user array snapshots, and emits a /orgChangeLog
 * entry. The page itself never writes directly — keeps the audit trail
 * consistent.
 *
 * Permission gate (defence in depth):
 *   - Page hides itself if !(isMasterOrg && currentOrgRole === 'ADMIN').
 *   - Firestore rules enforce server-side.
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Input, Spinner, Table, Badge, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label } from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { useAuthStore } from '../../store/auth.store';
import { useToast } from '../../contexts/ToastContext';
import {
  loadEntities,
  loadOwnerCandidates,
  createEntity,
  renameOrg,
  changeBroadcasterParent,
  updateAcceptedLanguages,
  changeOrgCategory,
  assignOwner,
  deleteEntity,
  EntityDoc,
  EntityKind,
} from '../../services/globalOrgManager';
import { loadChangeLog, OrgChangeLogEntry } from '../../services/orgChangeLog';
import { useCollectionCache } from '../../services/collectionCache.service';
import { categoryToType, getCategoryLabel, getTypeLabel, OrgCategory } from '../../store/organization.store';

const LANGUAGE_OPTIONS = [
  'english', 'afrikaans', 'zulu', 'xhosa', 'sotho', 'tswana',
  'pedi', 'venda', 'tsonga', 'swazi', 'ndebele',
  'portuguese', 'french', 'other',
];

// All seven subcategories from src/store/organization.store.ts. The top-level
// `type` is derived from this via `categoryToType()` — we never let the two
// drift out of sync, since downstream feature gating reads `type` to decide
// what UI to show.
const ORG_CATEGORY_OPTIONS: OrgCategory[] = [
  'broadcaster',
  'mediahouse',
  'agency',
  'brand',
  'production',
  'artist',
  'publisher',
];

/**
 * Maps an OrgCategory to the EntityKind that should own the link doc.
 *
 *   broadcaster                            → 'broadcaster'  (/broadcaster link)
 *   mediahouse                             → 'mediaowner'   (/mediaowner link)
 *   agency, brand, production,             → 'org'          (/agency, /client,
 *   artist, publisher                                        or /artists-global)
 *
 * Used by the Create modal so the user's chosen subcategory drives the
 * data layer, not the tab they happened to open the modal from. Otherwise
 * picking "Agency" on the Media Houses tab silently lands the entry in
 * /mediaowner instead of /agency.
 */
const categoryToKind = (category: OrgCategory): EntityKind => {
  if (category === 'broadcaster') return 'broadcaster';
  if (category === 'mediahouse') return 'mediaowner';
  return 'org';
};

type Tab = 'mediaowners' | 'broadcasters' | 'otherorgs' | 'log';

const GlobalOrganizationManager: React.FC = () => {
  const firebase = useContext(FirebaseContext);
  const auth = useAuthStore((s) => s.auth);
  const isMasterOrg = useOrganizationStore((s) => s.isMasterOrg);
  const currentOrgRole = useOrganizationStore((s) => s.currentOrgRole);
  const isSuperAdmin = useOrganizationStore((s) => s.isSuperAdmin);
  const { showSuccess, showError } = useToast();

  const actorUid = auth?.uid || '';
  const actorName = auth?.displayName || auth?.email || '';

  const canAccess = isSuperAdmin || (isMasterOrg && currentOrgRole === 'ADMIN');

  // --- Data load ---------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [broadcasters, setBroadcasters] = useState<EntityDoc[]>([]);
  const [mediaowners, setMediaowners] = useState<EntityDoc[]>([]);
  const [otherOrgs, setOtherOrgs] = useState<EntityDoc[]>([]);
  const [orgIndex, setOrgIndex] = useState<Record<string, any>>({});
  const [owners, setOwners] = useState<Array<{ uid: string; displayName: string; email: string }>>([]);
  const [logEntries, setLogEntries] = useState<OrgChangeLogEntry[]>([]);

  const reload = async () => {
    if (!firebase) return;
    setLoading(true);
    try {
      const [entities, ownerList, log] = await Promise.all([
        loadEntities(firebase.db),
        loadOwnerCandidates(firebase.db),
        loadChangeLog(firebase.db, { pageSize: 100 }),
      ]);
      setBroadcasters(entities.broadcasters);
      setMediaowners(entities.mediaowners);
      setOtherOrgs(entities.otherOrgs);
      setOrgIndex(entities.orgIndex);
      setOwners(ownerList);
      setLogEntries(log);
    } catch (err: any) {
      showError(err.message || 'Failed to load global org data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh the actor's own organization list in the store after any
  // create/delete so the OrganizationSwitcher picker reflects the change
  // without requiring a hard refresh. The service layer already updates
  // /users/{uid}.organizations[] server-side; this just re-reads it into
  // local state. setOrganizations() handles the "current org no longer
  // valid" case internally (clears selection so the picker reopens).
  const refreshActorOrgs = async () => {
    if (!firebase || !actorUid) return;
    try {
      const orgs = await firebase.getUserOrganizations(actorUid);
      useOrganizationStore.getState().setOrganizations(orgs);
    } catch (err) {
      console.warn('Failed to refresh actor organizations after GOM mutation:', err);
    }
  };

  // Refresh the broadcaster + mediaowner global cache after any mutation
  // here so the chase-list parser, AcceptedLanguagesEditor, and JobDetail
  // language-acceptance check immediately see GOM changes without a hard
  // refresh. Cheap: two getDocs against small collections.
  const refreshGlobalEntriesCache = async () => {
    if (!firebase) return;
    await useCollectionCache.getState().refreshGlobalEntries(firebase);
  };

  useEffect(() => {
    if (canAccess) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, firebase]);

  // --- UI state ----------------------------------------------------------
  const [tab, setTab] = useState<Tab>('mediaowners');
  const [searchQuery, setSearchQuery] = useState('');
  const [parentFilter, setParentFilter] = useState<string>(''); // orgId or ''
  const [editingEntity, setEditingEntity] = useState<EntityDoc | null>(null);
  const [editingKind, setEditingKind] = useState<EntityKind>('broadcaster');
  const [creating, setCreating] = useState<EntityKind | null>(null);
  const [deletingEntity, setDeletingEntity] = useState<EntityDoc | null>(null);
  const [deletingKind, setDeletingKind] = useState<EntityKind>('broadcaster');

  // --- Filtered lists ----------------------------------------------------
  const visibleMediaowners = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return mediaowners
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [mediaowners, searchQuery]);

  const visibleBroadcasters = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return broadcasters
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .filter((e) => !parentFilter || e.parentMediaOwnerOrgId === parentFilter || (parentFilter === '__none__' && !e.parentMediaOwnerOrgId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [broadcasters, searchQuery, parentFilter]);

  // Other Orgs tab: an /organizations doc with no broadcaster/mediaowner link
  // — artists, agencies, brands, productions, publishers. The category
  // filter lets master admins narrow to a single subcategory.
  const [otherOrgsCategoryFilter, setOtherOrgsCategoryFilter] = useState<string>('');
  const visibleOtherOrgs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return otherOrgs
      .filter((e) => !q || e.name.toLowerCase().includes(q))
      .filter((e) => !otherOrgsCategoryFilter || e.category === otherOrgsCategoryFilter)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [otherOrgs, searchQuery, otherOrgsCategoryFilter]);

  // --- Access guard ------------------------------------------------------
  if (!canAccess) {
    return (
      <div className="text-center py-5">
        <i className="tim-icons icon-lock-circle text-warning" style={{ fontSize: '3rem' }}></i>
        <h4 className="mt-3 text-white">Access denied</h4>
        <p className="text-muted">This page is restricted to Master Organization admins.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: '0.75rem' }}>
        <div>
          <h4 className="text-white mb-0">Global Organization Manager</h4>
          <small className="text-muted">
            Master pool of media houses, broadcasters, and their parent links. Every change is logged.
          </small>
        </div>
        <div>
          <Button size="sm" className="btn-info-custom mr-2" onClick={reload} disabled={loading}>
            <span style={{ position: 'relative', zIndex: 2 }}>
              {loading ? <Spinner size="sm" /> : <><i className="tim-icons icon-refresh-01 mr-1"></i>Refresh</>}
            </span>
          </Button>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1rem',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        paddingBottom: '0.5rem',
      }}>
        <TabButton active={tab === 'mediaowners'} onClick={() => setTab('mediaowners')} label="Media Houses" count={mediaowners.length} />
        <TabButton active={tab === 'broadcasters'} onClick={() => setTab('broadcasters')} label="Broadcasters" count={broadcasters.length} />
        <TabButton active={tab === 'otherorgs'} onClick={() => setTab('otherorgs')} label="Other Organizations" count={otherOrgs.length} />
        <TabButton active={tab === 'log'} onClick={() => setTab('log')} label="Change Log" count={logEntries.length} />
      </div>

      {/* Filters (only on entity tabs) */}
      {tab !== 'log' && (
        <div className="d-flex mb-3" style={{ gap: '0.5rem' }}>
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search by name..."
            style={{ maxWidth: '260px' }}
          />
          {tab === 'broadcasters' && (
            <Input
              bsSize="sm"
              type="select"
              value={parentFilter}
              onChange={(e) => setParentFilter(e.target.value)}
              style={{ maxWidth: '260px' }}
            >
              <option value="">All media houses</option>
              <option value="__none__">— No parent (orphans)</option>
              {mediaowners.map((m) => <option key={m.orgId} value={m.orgId}>{m.name}</option>)}
            </Input>
          )}
          {tab === 'otherorgs' && (
            <Input
              bsSize="sm"
              type="select"
              value={otherOrgsCategoryFilter}
              onChange={(e) => setOtherOrgsCategoryFilter(e.target.value)}
              style={{ maxWidth: '220px' }}
            >
              <option value="">All categories</option>
              <option value="artist">{getCategoryLabel('artist' as OrgCategory)}</option>
              <option value="publisher">{getCategoryLabel('publisher' as OrgCategory)}</option>
              <option value="agency">{getCategoryLabel('agency' as OrgCategory)}</option>
              <option value="brand">{getCategoryLabel('brand' as OrgCategory)}</option>
              <option value="production">{getCategoryLabel('production' as OrgCategory)}</option>
            </Input>
          )}
          <div className="ml-auto">
            <Button size="sm" className="btn-success-custom" onClick={() => setCreating(
              tab === 'broadcasters' ? 'broadcaster'
              : tab === 'mediaowners' ? 'mediaowner'
              : 'org')}>
              <span style={{ position: 'relative', zIndex: 2 }}>
                <i className="tim-icons icon-simple-add mr-1"></i>
                New {tab === 'broadcasters' ? 'Broadcaster' : tab === 'mediaowners' ? 'Media House' : 'Organization'}
              </span>
            </Button>
          </div>
        </div>
      )}

      {/* Tables */}
      {loading ? (
        <div className="text-center py-4"><Spinner /></div>
      ) : tab === 'mediaowners' ? (
        <MediaOwnersTable
          items={visibleMediaowners}
          broadcasters={broadcasters}
          onEdit={(e) => { setEditingKind('mediaowner'); setEditingEntity(e); }}
          onDelete={(e) => { setDeletingKind('mediaowner'); setDeletingEntity(e); }}
        />
      ) : tab === 'broadcasters' ? (
        <BroadcastersTable
          items={visibleBroadcasters}
          onEdit={(e) => { setEditingKind('broadcaster'); setEditingEntity(e); }}
          onDelete={(e) => { setDeletingKind('broadcaster'); setDeletingEntity(e); }}
        />
      ) : tab === 'otherorgs' ? (
        <OtherOrgsTable
          items={visibleOtherOrgs}
          onEdit={(e) => { setEditingKind('org'); setEditingEntity(e); }}
          onDelete={(e) => { setDeletingKind('org'); setDeletingEntity(e); }}
        />
      ) : (
        <ChangeLogTable entries={logEntries} />
      )}

      {/* Edit modal */}
      {editingEntity && (
        <EditEntityModal
          isOpen={!!editingEntity}
          kind={editingKind}
          entity={editingEntity}
          mediaowners={mediaowners}
          owners={owners}
          orgIndex={orgIndex}
          actorUid={actorUid}
          actorName={actorName}
          firebase={firebase}
          onClose={() => setEditingEntity(null)}
          onSaved={async () => { setEditingEntity(null); await reload(); await refreshGlobalEntriesCache(); showSuccess('Saved'); }}
          onError={(err) => showError(err.message || 'Save failed')}
        />
      )}

      {/* Create modal */}
      {creating && (
        <CreateEntityModal
          isOpen={!!creating}
          kind={creating}
          mediaowners={mediaowners}
          owners={owners}
          actorUid={actorUid}
          actorName={actorName}
          firebase={firebase}
          onClose={() => setCreating(null)}
          onCreated={async () => { setCreating(null); await reload(); await refreshActorOrgs(); await refreshGlobalEntriesCache(); showSuccess('Created'); }}
          onError={(err) => showError(err.message || 'Create failed')}
        />
      )}

      {/* Delete confirm */}
      {deletingEntity && (
        <DeleteConfirmModal
          isOpen={!!deletingEntity}
          kind={deletingKind}
          entity={deletingEntity}
          broadcasters={broadcasters}
          actorUid={actorUid}
          actorName={actorName}
          firebase={firebase}
          onClose={() => setDeletingEntity(null)}
          onDeleted={async (result) => {
            setDeletingEntity(null);
            await reload();
            await refreshActorOrgs();
            await refreshGlobalEntriesCache();
            showSuccess(`Deleted${result.orphanedBroadcasters ? ` (${result.orphanedBroadcasters} broadcaster(s) orphaned)` : ''}`);
          }}
          onError={(err) => showError(err.message || 'Delete failed')}
        />
      )}
    </div>
  );
};

// --- Sub-components -------------------------------------------------------

const MediaOwnersTable: React.FC<{
  items: EntityDoc[];
  broadcasters: EntityDoc[];
  onEdit: (e: EntityDoc) => void;
  onDelete: (e: EntityDoc) => void;
}> = ({ items, broadcasters, onEdit, onDelete }) => {
  const broadcasterCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const b of broadcasters) {
      if (b.parentMediaOwnerOrgId) m[b.parentMediaOwnerOrgId] = (m[b.parentMediaOwnerOrgId] || 0) + 1;
    }
    return m;
  }, [broadcasters]);

  if (items.length === 0) {
    return <div className="text-muted text-center py-4">No media houses match the filter.</div>;
  }

  return (
    <Table responsive dark size="sm" style={{ fontSize: '0.85rem' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Category</th>
          <th>Type</th>
          <th>Linked Broadcasters</th>
          <th style={{ width: '140px' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.orgId}>
            <td><strong>{e.name}</strong><br /><small className="text-muted">{e.orgId}</small></td>
            <td>{e.category ? getCategoryLabel(e.category as OrgCategory) : '—'}</td>
            <td>{getTypeLabel(e.type)}</td>
            <td>{broadcasterCounts[e.orgId] || 0}</td>
            <td>
              <Button size="sm" className="btn-info-custom mr-1" onClick={() => onEdit(e)}>
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-pencil"></i>
                </span>
              </Button>
              <Button size="sm" color="danger" onClick={() => onDelete(e)}>
                <i className="tim-icons icon-trash-simple"></i>
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const BroadcastersTable: React.FC<{
  items: EntityDoc[];
  onEdit: (e: EntityDoc) => void;
  onDelete: (e: EntityDoc) => void;
}> = ({ items, onEdit, onDelete }) => {
  if (items.length === 0) {
    return <div className="text-muted text-center py-4">No broadcasters match the filter.</div>;
  }
  return (
    <Table responsive dark size="sm" style={{ fontSize: '0.85rem' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Category</th>
          <th>Media House</th>
          <th>Languages</th>
          <th style={{ width: '140px' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.orgId}>
            <td><strong>{e.name}</strong><br /><small className="text-muted">{e.orgId}</small></td>
            <td>{e.category || '—'}</td>
            <td>
              {e.parentMediaOwnerName ? (
                <span>{e.parentMediaOwnerName}</span>
              ) : (
                <Badge color="warning">No media house</Badge>
              )}
            </td>
            <td>
              {(e.acceptedLanguages || []).length === 0
                ? <span className="text-muted">—</span>
                : (e.acceptedLanguages || []).map((l) => <Badge key={l} color="secondary" className="mr-1">{l}</Badge>)}
            </td>
            <td>
              <Button size="sm" className="btn-info-custom mr-1" onClick={() => onEdit(e)}>
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-pencil"></i>
                </span>
              </Button>
              <Button size="sm" color="danger" onClick={() => onDelete(e)}>
                <i className="tim-icons icon-trash-simple"></i>
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

/** Renders the "Other Organizations" tab — bare /organizations docs with no
 *  /broadcaster or /mediaowner link. Columns: name, category (badge), type.
 *  No parent / language columns since those concepts only apply to
 *  broadcasters. */
const OtherOrgsTable: React.FC<{
  items: EntityDoc[];
  onEdit: (e: EntityDoc) => void;
  onDelete: (e: EntityDoc) => void;
}> = ({ items, onEdit, onDelete }) => {
  if (items.length === 0) {
    return <div className="text-muted text-center py-4">No organizations match the filter.</div>;
  }
  return (
    <Table responsive dark size="sm" style={{ fontSize: '0.85rem' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th>Subcategory</th>
          <th>Type</th>
          <th style={{ width: '140px' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((e) => (
          <tr key={e.orgId}>
            <td><strong>{e.name}</strong><br /><small className="text-muted">{e.orgId}</small></td>
            <td>{e.category ? getCategoryLabel(e.category as OrgCategory) : <span className="text-muted">—</span>}</td>
            <td>{getTypeLabel(e.type)}</td>
            <td>
              <Button size="sm" className="btn-info-custom mr-1" onClick={() => onEdit(e)}>
                <span style={{ position: 'relative', zIndex: 2 }}>
                  <i className="tim-icons icon-pencil"></i>
                </span>
              </Button>
              <Button size="sm" color="danger" onClick={() => onDelete(e)}>
                <i className="tim-icons icon-trash-simple"></i>
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const ChangeLogTable: React.FC<{ entries: OrgChangeLogEntry[] }> = ({ entries }) => {
  if (entries.length === 0) {
    return <div className="text-muted text-center py-4">No changes logged yet.</div>;
  }
  return (
    <Table responsive dark size="sm" style={{ fontSize: '0.85rem' }}>
      <thead>
        <tr><th>When</th><th>Action</th><th>By</th><th>Details</th></tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td>{new Date(e.createdAt).toLocaleString()}</td>
            <td><Badge color="dark">{e.action}</Badge></td>
            <td>{e.actorName || e.actorUid}</td>
            <td>{e.message}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

// --- Shared tab button + search input -------------------------------------

/** Clear button-style tab. Active state uses a solid accent background so
 *  the selection is obvious at a glance; inactive tabs are outlined and
 *  brighten on hover to advertise that they're clickable. */
const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}> = ({ active, onClick, label, count }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        cursor: 'pointer',
        padding: '7px 16px',
        borderRadius: '6px',
        fontSize: '0.85rem',
        fontWeight: active ? 700 : 500,
        letterSpacing: '0.02em',
        border: active
          ? '1px solid var(--accent-1-color, #6b6bff)'
          : `1px solid ${hover ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.15)'}`,
        background: active
          ? 'var(--accent-1-color, #6b6bff)'
          : hover ? 'rgba(255,255,255,0.05)' : 'transparent',
        color: active ? '#ffffff' : hover ? '#ffffff' : 'rgba(255,255,255,0.65)',
        transition: 'all 0.12s ease-in-out',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
      }}
    >
      {label}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '20px',
          height: '18px',
          padding: '0 6px',
          borderRadius: '999px',
          fontSize: '0.7rem',
          fontWeight: 700,
          background: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.12)',
          color: active ? '#ffffff' : 'rgba(255,255,255,0.75)',
        }}
      >
        {count}
      </span>
    </button>
  );
};

/** Search input with an inline × button to clear in one click. The button
 *  only renders when there's text in the field. */
const SearchInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
}> = ({ value, onChange, placeholder, style }) => {
  return (
    <div style={{ position: 'relative', display: 'inline-block', width: '100%', ...style }}>
      <Input
        bsSize="sm"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ paddingRight: value ? '30px' : undefined }}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Clear search"
          style={{
            position: 'absolute',
            top: '50%',
            right: '6px',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.45)',
            cursor: 'pointer',
            fontSize: '1rem',
            lineHeight: 1,
            padding: '2px 6px',
          }}
        >
          &times;
        </button>
      )}
    </div>
  );
};

// --- Shared chip picker for languages -------------------------------------

/** Visual chip picker for the broadcaster's accepted languages. Selected
 *  chips have a solid accent fill + check icon; unselected chips are
 *  outlined and muted. Header shows the running selected count so the user
 *  can confirm at a glance what's active. */
const LanguageChips: React.FC<{
  selected: string[];
  onToggle: (lang: string) => void;
}> = ({ selected, onToggle }) => {
  return (
    <>
      <div className="text-muted small mb-2">
        {selected.length === 0
          ? 'None selected. Click a language to add it.'
          : `${selected.length} selected: ${selected.join(', ')}`}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {LANGUAGE_OPTIONS.map((lang) => {
          const isSelected = selected.includes(lang);
          return (
            <button
              key={lang}
              type="button"
              onClick={() => onToggle(lang)}
              style={{
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: '999px',
                fontSize: '0.78rem',
                fontWeight: 600,
                letterSpacing: '0.02em',
                border: isSelected
                  ? '1px solid var(--accent-1-color, #6b6bff)'
                  : '1px solid rgba(255,255,255,0.18)',
                background: isSelected
                  ? 'var(--accent-1-color, #6b6bff)'
                  : 'transparent',
                color: isSelected ? '#ffffff' : 'rgba(255,255,255,0.55)',
                transition: 'all 0.12s ease-in-out',
              }}
            >
              {isSelected && <i className="tim-icons icon-check-2 mr-1" style={{ fontSize: '0.7rem' }}></i>}
              {lang}
            </button>
          );
        })}
      </div>
    </>
  );
};

// --- Edit modal -----------------------------------------------------------

const EditEntityModal: React.FC<{
  isOpen: boolean;
  kind: EntityKind;
  entity: EntityDoc;
  mediaowners: EntityDoc[];
  owners: Array<{ uid: string; displayName: string; email: string }>;
  orgIndex: Record<string, any>;
  actorUid: string;
  actorName: string;
  firebase: any;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: any) => void;
}> = ({ isOpen, kind, entity, mediaowners, owners, actorUid, actorName, firebase, onClose, onSaved, onError }) => {
  const [name, setName] = useState(entity.name);
  // Default to the org's existing category. Fall back to a sensible default
  // matching which collection it lives in if no category was set.
  const [category, setCategory] = useState<OrgCategory>(
    (entity.category as OrgCategory) || (kind === 'broadcaster' ? 'broadcaster' : 'mediahouse'),
  );
  const [parentOrgId, setParentOrgId] = useState<string>(entity.parentMediaOwnerOrgId || '');
  const [languages, setLanguages] = useState<string[]>(entity.acceptedLanguages || []);
  const [ownerUid, setOwnerUid] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const derivedType = categoryToType(category);

  const toggleLanguage = (lang: string) => {
    setLanguages((prev) => prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]);
  };

  const save = async () => {
    if (!firebase) return;
    setSaving(true);
    try {
      if (name.trim() !== entity.name) {
        await renameOrg(firebase.db, { orgId: entity.orgId, newName: name.trim(), actorUid, actorName });
      }
      if (category !== entity.category) {
        await changeOrgCategory(firebase.db, { orgId: entity.orgId, newCategory: category, actorUid, actorName });
      }
      if (kind === 'broadcaster') {
        const desiredParent = parentOrgId || null;
        if (desiredParent !== (entity.parentMediaOwnerOrgId || null)) {
          await changeBroadcasterParent(firebase.db, { linkId: entity.linkId, newParentOrgId: desiredParent, actorUid, actorName });
        }
        await updateAcceptedLanguages(firebase.db, { linkId: entity.linkId, languages, actorUid, actorName });
      }
      if (ownerUid) {
        const owner = owners.find((o) => o.uid === ownerUid);
        await assignOwner(firebase.db, {
          orgId: entity.orgId,
          orgName: name.trim() || entity.name,
          orgCategory: category,
          ownerUid,
          ownerName: owner?.displayName,
          actorUid,
          actorName,
        });
        // Reset so the dropdown returns to "— Don't add anyone" rather
        // than keeping the just-added user selected.
        setOwnerUid('');
      }
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} contentClassName="delete-confirm-modal" size="lg">
      <ModalHeader toggle={onClose}>
        Edit {kind === 'broadcaster' ? 'Broadcaster' : kind === 'mediaowner' ? 'Media House' : 'Organization'}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Subcategory</Label>
          <Input type="select" value={category} onChange={(e) => setCategory(e.target.value as OrgCategory)}>
            {ORG_CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{getCategoryLabel(c)}</option>)}
          </Input>
          <small className="text-muted">
            Top-level type is derived automatically: <strong>{getTypeLabel(derivedType)}</strong>
          </small>
        </FormGroup>
        {kind === 'broadcaster' && (
          <>
            <FormGroup>
              <Label>Parent Media House</Label>
              <Input type="select" value={parentOrgId} onChange={(e) => setParentOrgId(e.target.value)}>
                <option value="">— No media house</option>
                {mediaowners.map((m) => <option key={m.orgId} value={m.orgId}>{m.name}</option>)}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Accepted Languages</Label>
              <LanguageChips selected={languages} onToggle={toggleLanguage} />
            </FormGroup>
          </>
        )}
        <FormGroup>
          <Label>Add as ADMIN (optional)</Label>
          <Input type="select" value={ownerUid} onChange={(e) => setOwnerUid(e.target.value)}>
            <option value="">— Don't add anyone</option>
            {owners.map((o) => <option key={o.uid} value={o.uid}>{o.displayName} ({o.email})</option>)}
          </Input>
          <small className="text-muted">
            Adds the selected user to this org with the ADMIN role. Existing members are not removed.
            Candidates are limited to ADMINs of any Master Org.
          </small>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button className="btn-primary-custom" onClick={onClose} disabled={saving}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
        <Button className="btn-success-custom" onClick={save} disabled={saving}>
          <span style={{ position: 'relative', zIndex: 2 }}>
            {saving ? <Spinner size="sm" /> : 'Save Changes'}
          </span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// --- Create modal ---------------------------------------------------------

const CreateEntityModal: React.FC<{
  isOpen: boolean;
  kind: EntityKind;
  mediaowners: EntityDoc[];
  owners: Array<{ uid: string; displayName: string; email: string }>;
  actorUid: string;
  actorName: string;
  firebase: any;
  onClose: () => void;
  onCreated: () => void;
  onError: (err: any) => void;
}> = ({ isOpen, kind, mediaowners, owners, actorUid, actorName, firebase, onClose, onCreated, onError }) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<OrgCategory>(
    kind === 'broadcaster' ? 'broadcaster'
    : kind === 'mediaowner' ? 'mediahouse'
    : 'artist',
  );
  const [parentOrgId, setParentOrgId] = useState<string>('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [ownerUid, setOwnerUid] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const derivedType = categoryToType(category);
  // The user's chosen subcategory dictates where the link doc goes — not
  // the tab the modal was launched from. Picking "Agency" on the Media
  // Houses tab should land the entry in /agency, not /mediaowner.
  const effectiveKind = categoryToKind(category);
  const kindMismatch = effectiveKind !== kind;

  const toggleLanguage = (lang: string) => {
    setLanguages((prev) => prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]);
  };

  const save = async () => {
    if (!firebase) return;
    if (!name.trim()) { onError(new Error('Name is required')); return; }
    if (!ownerUid) { onError(new Error('Owner is required')); return; }
    setSaving(true);
    try {
      const owner = owners.find((o) => o.uid === ownerUid);
      await createEntity(firebase.db, {
        kind: effectiveKind,
        name: name.trim(),
        category,
        ownerUid,
        ownerName: owner?.displayName,
        ownerEmail: owner?.email,
        parentMediaOwnerOrgId: effectiveKind === 'broadcaster' ? (parentOrgId || null) : null,
        acceptedLanguages: effectiveKind === 'broadcaster' ? languages : undefined,
        actorUid,
        actorName,
      });
      onCreated();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} contentClassName="delete-confirm-modal" size="lg">
      <ModalHeader toggle={onClose}>
        New {effectiveKind === 'broadcaster' ? 'Broadcaster' : effectiveKind === 'mediaowner' ? 'Media House' : 'Organization'}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Highveld Radio (Pty) Ltd" />
        </FormGroup>
        <FormGroup>
          <Label>Subcategory</Label>
          <Input type="select" value={category} onChange={(e) => setCategory(e.target.value as OrgCategory)}>
            {ORG_CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{getCategoryLabel(c)}</option>)}
          </Input>
          <small className="text-muted">
            Top-level type is derived automatically: <strong>{getTypeLabel(derivedType)}</strong>
          </small>
          {kindMismatch && (
            <small className="d-block mt-1" style={{ color: '#ffb432' }}>
              Heads-up: subcategory <strong>{getCategoryLabel(category)}</strong> doesn’t belong on the
              {' '}{kind === 'broadcaster' ? 'Broadcasters' : kind === 'mediaowner' ? 'Media Houses' : 'Other Organizations'} tab.
              It will be created as a {effectiveKind === 'broadcaster' ? 'broadcaster' : effectiveKind === 'mediaowner' ? 'media house' : 'standalone organization'} and appear on the
              {' '}{effectiveKind === 'broadcaster' ? 'Broadcasters' : effectiveKind === 'mediaowner' ? 'Media Houses' : 'Other Organizations'} tab.
            </small>
          )}
        </FormGroup>
        {effectiveKind === 'broadcaster' && (
          <>
            <FormGroup>
              <Label>Parent Media House</Label>
              <Input type="select" value={parentOrgId} onChange={(e) => setParentOrgId(e.target.value)}>
                <option value="">— No media house</option>
                {mediaowners.map((m) => <option key={m.orgId} value={m.orgId}>{m.name}</option>)}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Accepted Languages</Label>
              <LanguageChips selected={languages} onToggle={toggleLanguage} />
            </FormGroup>
          </>
        )}
        <FormGroup>
          <Label>Owner *</Label>
          <Input type="select" value={ownerUid} onChange={(e) => setOwnerUid(e.target.value)}>
            <option value="">— Select an owner</option>
            {owners.map((o) => <option key={o.uid} value={o.uid}>{o.displayName} ({o.email})</option>)}
          </Input>
          <small className="text-muted">
            The owner becomes the first ADMIN of the new org. Candidates are limited to ADMINs of any Master Org.
          </small>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button className="btn-primary-custom" onClick={onClose} disabled={saving}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
        <Button className="btn-success-custom" onClick={save} disabled={saving}>
          <span style={{ position: 'relative', zIndex: 2 }}>
            {saving ? <Spinner size="sm" /> : 'Create'}
          </span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// --- Delete confirm -------------------------------------------------------

const DeleteConfirmModal: React.FC<{
  isOpen: boolean;
  kind: EntityKind;
  entity: EntityDoc;
  broadcasters: EntityDoc[];
  actorUid: string;
  actorName: string;
  firebase: any;
  onClose: () => void;
  onDeleted: (result: { orphanedBroadcasters: number; removedUserMemberships: number }) => void;
  onError: (err: any) => void;
}> = ({ isOpen, kind, entity, broadcasters, actorUid, actorName, firebase, onClose, onDeleted, onError }) => {
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const orphansImpacted = kind === 'mediaowner' ? broadcasters.filter((b) => b.parentMediaOwnerOrgId === entity.orgId).length : 0;
  const expected = entity.name;
  const matched = confirmText.trim() === expected;

  const doDelete = async () => {
    if (!firebase || !matched) return;
    setDeleting(true);
    try {
      const result = await deleteEntity(firebase.db, { kind, orgId: entity.orgId, linkId: entity.linkId, actorUid, actorName });
      onDeleted(result);
    } catch (err) {
      onError(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} contentClassName="delete-confirm-modal">
      <ModalHeader toggle={onClose}>
        Delete {kind === 'broadcaster' ? 'broadcaster' : kind === 'mediaowner' ? 'media house' : 'organization'}
      </ModalHeader>
      <ModalBody>
        <p>You are about to delete <strong>{entity.name}</strong>. This will:</p>
        <ul>
          <li>Delete its <code>/organizations</code> doc, its <code>/{kind}</code> link doc, and the members subcollection.</li>
          <li>Remove the membership from every user's <code>organizations[]</code> array.</li>
          {kind === 'mediaowner' && (
            <li><strong>Orphan {orphansImpacted} broadcaster{orphansImpacted === 1 ? '' : 's'}</strong> currently parented to this media house (parent set to null).</li>
          )}
          <li>Append a change-log entry.</li>
        </ul>
        <FormGroup className="mt-3">
          <Label>Type <strong>{expected}</strong> to confirm:</Label>
          <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button className="btn-primary-custom" onClick={onClose} disabled={deleting}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
        <Button color="danger" onClick={doDelete} disabled={!matched || deleting}>
          {deleting ? <Spinner size="sm" /> : 'Delete permanently'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GlobalOrganizationManager;
